// src/components/charts/MerchandisingCharts.jsx
import React from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import DonutChart from '../agComponents/DonutChart';
import BarrasHorizontalesChart from './BarrasHorizontalesChart';
import kpiService from '../../services/kpiService';
import { formatCurrency, formatNumber } from '../../utils/formatters';

/**
 * Componente que agrupa los gráficos de Merchandising (Imagen 4)
 * - Métricas de merchandising (barras horizontales - izquierda)
 * - Gestión interna vs externa (dona - centro)
 * - Principales subgrupos (barras horizontales - derecha)
 */
const MerchandisingCharts = ({ fechaInicio, fechaFin, empresaId }) => {
  const [merchandisingBarras, setMerchandisingBarras] = React.useState(null);
  const [gestionData, setGestionData] = React.useState(null);
  const [subgruposData, setSubgruposData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Obtener métricas de merchandising
        const barrasResponse = await kpiService.getMerchandisingBarras({
          fechaInicio,
          fechaFin,
          empresaId,
        });

        // Obtener gestión interna vs externa
        const gestionResponse = await kpiService.getGestionInternaExterna({
          fechaInicio,
          fechaFin,
          empresaId,
        });

        // Obtener subgrupos
        const subgruposResponse = await kpiService.getSubgruposMerchandising({
          fechaInicio,
          fechaFin,
          empresaId,
        });

        if (barrasResponse.success && barrasResponse.data) {
          setMerchandisingBarras(barrasResponse.data);
        }

        if (gestionResponse.success && gestionResponse.data) {
  setGestionData(gestionResponse.data);
}


        if (subgruposResponse.success && subgruposResponse.data) {
          setSubgruposData(subgruposResponse.data);
        }
      } catch (err) {
        console.error('Error fetching merchandising data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fechaInicio, fechaFin, empresaId]);

  if (loading) {
    return (
      <Row className="g-3 mb-4">
        <Col xs={12} className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3 text-muted">Cargando datos...</p>
        </Col>
      </Row>
    );
  }

  const formatValue = (metrica, valor) => {
    if (!metrica) return valor || '0';
    if (metrica.includes('Total ventas')) {
      return formatCurrency(valor);
    } else if (metrica.includes('Ticket medio')) {
      return `${valor}€`;
    } else {
      return `${valor}%`;
    }
  };

  return (
    <Row className="g-3 mb-4">
      {/* Métricas de Merchandising - Barras con valores */}
      <Col lg={4}>
        {merchandisingBarras && Array.isArray(merchandisingBarras) && merchandisingBarras.length > 0 ? (
          <Card>
            <Card.Body>
              <h5 className="mb-4">Merchandising</h5>
              <div className="d-flex flex-column gap-4">
                {merchandisingBarras.map((item, idx) => {
                  const max = item.max || 1; // Evitar división por 0
                  const percentage = (item.valor / max) * 100;
                  return (
                    <div key={idx}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="small fw-medium">{item.metrica || 'Sin nombre'}</span>
                        <span className="fw-semibold">{formatValue(item.metrica, item.valor || 0)}</span>
                      </div>
                      <div className="position-relative" style={{ height: '24px' }}>
                        <div
                          className="position-absolute w-100 rounded"
                          style={{
                            height: '100%',
                            backgroundColor: '#e5e7eb',
                          }}
                        />
                        <div
                          className="position-absolute rounded"
                          style={{
                            height: '100%',
                            width: `${percentage}%`,
                            backgroundColor: item.color || '#3b82f6',
                            transition: 'width 0.6s ease-out',
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card.Body>
          </Card>
        ) : (
          <Card>
            <Card.Body>
              <h5 className="mb-4">Merchandising</h5>
              <p className="text-muted text-center py-4">No hay datos disponibles</p>
            </Card.Body>
          </Card>
        )}
      </Col>

      {/* Gestión interna vs gestión externa - Dona */}
      <Col lg={4}>
        {gestionData && gestionData.data && Array.isArray(gestionData.data) && gestionData.data.length > 0 ? (
          <Card>
            <Card.Body>
              <h5 className="mb-4">Gestión interna vs gestión externa</h5>
              <div className="text-center mb-3">
                <small className="text-muted">Áreas</small>
              </div>
              <DonutChart
                data={gestionData.data}
                angleKey="value"
                labelKey="label"
                height={300}
                innerRadiusRatio={0.65}
              />
              {/* Leyenda con porcentajes */}
              <div className="mt-3">
                {gestionData.data.map((item, idx) => (
                  <div key={idx} className="d-flex align-items-center justify-content-between mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          backgroundColor: item.color || '#3b82f6',
                        }}
                      />
                      <small>{item.label || 'Sin nombre'}</small>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="badge" style={{ backgroundColor: '#10b981' }}>
                        + {(item.value || 0).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        ) : (
          <Card>
            <Card.Body>
              <h5 className="mb-4">Gestión interna vs gestión externa</h5>
              <p className="text-muted text-center py-4">No hay datos disponibles</p>
            </Card.Body>
          </Card>
        )}
      </Col>

      {/* Principales subgrupos de Merchandising */}
      <Col lg={4}>
        {subgruposData && Array.isArray(subgruposData) && subgruposData.length > 0 ? (
    <BarrasHorizontalesChart
      title="Principales subgrupos de Merchandising"
      data={subgruposData.map(item => ({
        area: item.subgrupo,
        porcentaje: item.porcentaje,
        color: item.color
      }))}
      showPercentageLabel={true}
    />
  ) : (
          <Card>
            <Card.Body>
              <h5 className="mb-4">Principales subgrupos de Merchandising</h5>
              <p className="text-muted text-center py-4">No hay datos disponibles</p>
            </Card.Body>
          </Card>
        )}
      </Col>
    </Row>
  );
};

export default MerchandisingCharts;